/* 负责：
 *  - WebSocket 连接 / 心跳 / 重连
 *  - 在线人数 & 用户墙
 *  - 当前题目 / 倒计时 / 公布答案
 *  - 当前阶段：ready | playing | ended
 *  - RankingModal 轮次数据（示例预留）
 */

import { getOnlineStatus, startQuiz, getRecordRank } from "@/api/scoring/quiz";
import { Message } from "element-ui";

const WS_URL = "wss://xilecloud.xilehd.top:2346";
const HEART = 15000;
const RECONNECT = 5000;

const state = () => ({
  huodongId: "", // 当前活动
  phase: "ready", // ready | playing | ended
  onlineCount: 0,
  users: [], // 前 maxSlots 个
  maxSlots: 10,

  // 题目
  question: null,
  index: 0,
  total: 0,
  djs: 0,
  published: false,

  curRank: [],
  totalRank: [],

  // 弹窗
  summaryVisible: false,
  rankingVisible: false,

  // 内部
  _ws: null,
  _hb: null,
  _reconnectTimer: null,
  _startLoading: false,

  _rankTimer: null,
});
function replaceArrayKeepRef (target, fresh) {
  target.splice(0, target.length, ...fresh)
}
/* ---------------- mutations ---------------- */
const mutations = {
  SET_HUODONG_ID(st, id) {
    st.huodongId = id;
  },

  SET_USERS(st, { users, count }) {
    st.onlineCount = count;
    st.users = users.slice(0, st.maxSlots);
  },

  /* ---- 题目流转 ---- */
  SET_QUESTION(st, { question, djs, total, index }) {
    st.phase = "playing";
    st.question = question;
    st.djs = djs;
    st.published = false;
    st.total = total || st.total;
    st.index = index;
    st.curRank.length = 0;
  },
  SHOW_ANSWER(st, ans) {
    st.published = true;
    if (ans) st.question.answer = ans;
  },
  SET_ENDED(st) {
    st.phase = "ended";
  },

  /* ---- 排名 ---- */
  SET_RANK(state, { curRank, totalRank }) {
    replaceArrayKeepRef(state.curRank,   curRank)
    replaceArrayKeepRef(state.totalRank, totalRank)
  },

  /* ---- 弹窗 ---- */
  TOGGLE_SUMMARY(st, v) {
    st.summaryVisible = v;
  },
  TOGGLE_RANKING(st, v) {
    st.rankingVisible = v;
  },

  /* ---- WebSocket refs ---- */
  SET_WS(st, ws) {
    st._ws = ws;
  },
  SET_HB(st, hb) {
    st._hb = hb;
  },
  SET_RECONN_T(st, t) {
    st._reconnectTimer = t;
  },
  SET_START_LOADING(st, v) {
    st._startLoading = v;
  },
  SET_RANK_TIMER(st, t) {
    st._rankTimer = t;
  },
};

/* ---------------- actions ---------------- */
const actions = {
  /* ========= WebSocket ========= */
  initWS({ state, commit, dispatch }, { huodongId }) {
    commit("SET_HUODONG_ID", huodongId);

    function login() {
      if (!state._ws || state._ws.readyState !== WebSocket.OPEN) return;
      state._ws.send(JSON.stringify({ type: "login", huodong_id: huodongId }));
    }
    function startHeart() {
      stopHeart();
      const hb = setInterval(() => {
        if (state._ws?.readyState === WebSocket.OPEN) {
          state._ws.send(JSON.stringify({ type: "ping" }));
        }
      }, HEART);
      commit("SET_HB", hb);
    }
    function stopHeart() {
      if (state._hb) {
        clearInterval(state._hb);
        commit("SET_HB", null);
      }
    }
    function reconnect() {
      if (state._reconnectTimer) return;
      const t = setTimeout(() => {
        commit("SET_RECONN_T", null);
        dispatch("initWS", { huodongId });
      }, RECONNECT);
      commit("SET_RECONN_T", t);
    }

    const ws = new WebSocket(WS_URL);
    commit("SET_WS", ws);

    ws.onopen = () => {
      console.log("[ws] onopen");
      login();
      startHeart();
    };
    ws.onerror = (e) => {
      console.error("[ws] onerror", e);
    };
    ws.onclose = (e) => {
      console.warn("[ws] onclose", e);
      stopHeart();
      reconnect();
    };
    ws.onmessage = ({ data }) => {
      let msg;
      try {
        msg = JSON.parse(data);
      } catch (_) {
        return;
      }
      dispatch("handleWS", msg);
    };
  },

  handleWS({ commit, dispatch }, msg) {
    const qid = msg.question?.id || msg.question_id;
    const rid = msg.question?.rotate_id || msg.rotate_id;
    switch (msg.type) {
      case "gameisdjs":
        commit("SET_QUESTION", {
          question: normalizeQuestion(msg.question),
          djs: msg.djs,
          total: msg.total_questions,
          index: msg.current_index,
        });
        if (qid && rid) {
          dispatch("startRankPolling", { qid, rid });
        }
        break;
      case "show_answer":
        commit(
          "SHOW_ANSWER",
          msg.yesanswer
            ? msg.yesanswer.split("").map((x) => x.toUpperCase())
            : null
        );
        if (qid && rid) {
          dispatch("fetchRank", { qid, rid });
          dispatch("stopRankPolling");
        }
        break;
      case "gameend":
        commit("SET_ENDED");
        commit("TOGGLE_SUMMARY", true);
        break;
      case "rank_update":
        commit("SET_RANK", msg);
        break;
      default:
        break;
    }
  },

  /** 每 N 秒拉一次本题/本轮排行榜 */
  startRankPolling({ state, commit, dispatch }, { qid, rid }) {
    dispatch("stopRankPolling");
    const poll = () => dispatch("fetchRank", { qid, rid });
    poll();
    const timer = setInterval(poll, 2000);
    commit("SET_RANK_TIMER", timer);
  },

  stopRankPolling({ state, commit }) {
    if (state._rankTimer) {
      clearInterval(state._rankTimer);
      commit("SET_RANK_TIMER", null);
    }
  },
  /* ========= 在线人数轮询 ========= */
  async fetchOnline({ state, commit }) {
    if (!state.huodongId) return;
    try {
      const res = await getOnlineStatus({ huodong_id: state.huodongId });
      const ok = res.data && (res.data.code === 0 || res.data.code === 1);
      if (ok) {
        commit("SET_USERS", {
          users: res.data.data.users || [],
          count: res.data.data.online_count || 0,
        });
      }
    } catch (_) {}
  },

  /* ========= 点击开始答题 ========= */
  async startQuiz({ state, commit, dispatch }) {
    if (state._startLoading) return;
    commit("SET_START_LOADING", true);

    try {
      const res = await startQuiz(state.huodongId);

      if (res && res.data && res.data.code === 1) {
        const fallback = setTimeout(() => {
          if (state.phase !== "playing") {
            commit("SET_QUESTION", {
              question: { title: "等待服务器题目...", options: [], answer: [] },
              djs: 0,
            });
          }
          commit("SET_START_LOADING", false);
        }, 3000);
      } else {
        commit("SET_START_LOADING", false);
        Message.info((res && res.data && res.data.msg) || "无法开始答题");
      }
    } catch (err) {
      commit("SET_START_LOADING", false);
      Message.info(err);
    }
  },

  async fetchRank({ state, commit }, { qid, rid }) {
    const [curRes, totalRes] = await Promise.all([
      getRecordRank({
        huodong_id: state.huodongId,
        questionid: qid,
        rotate_id: rid,
      }),
      getRecordRank({ huodong_id: state.huodongId, rotate_id: rid }),
    ]).catch(() => [null, null]);

    const sortByRightCount = (a, b) => b.right_count - a.right_count;

    const transform = (list) =>
      (list || [])
        .slice()
        .sort(sortByRightCount)
        .map((u, i) => ({
          user_id: u.user_id,
          nickname: u.member?.nickname || "游客",
          avatar: u.member?.headimg || "",
          time: u.total_time ? (u.total_time / 1000).toFixed(3) : "0.000",
          rank: i + 1,
          right_count: u.right_count,
          count: u.right_count,
        }));

    commit("SET_RANK", {
      curRank: curRes?.data?.code === 1 ? transform(curRes.data.data) : [],
      totalRank:
        totalRes?.data?.code === 1 ? transform(totalRes.data.data) : [],
    });
  },

  /* ========= 弹窗 helper ========= */
  openSummary({ commit }) {
    commit("TOGGLE_SUMMARY", true);
  },
  closeSummary({ commit }) {
    commit("TOGGLE_SUMMARY", false);
  },
  openRanking({ commit }) {
    commit("TOGGLE_RANKING", true);
  },
  closeRanking({ commit }) {
    commit("TOGGLE_RANKING", false);
  },
};

/* ---------------- getters ---------------- */
const getters = {
  /* mapState 用 getters 方便解构 */
  quizPhase: (s) => s.phase,
  onlineCount: (s) => s.onlineCount,
  readyUsers: (s) => s.users,
  question: (s) => s.question,
  djs: (s) => s.djs,
  published: (s) => s.published,
  curIndex: (s) => s.index,
  totalQuestion: (s) => s.total,
  summaryVisible: (s) => s.summaryVisible,
  rankingVisible: (s) => s.rankingVisible,
  startLoading: (s) => s._startLoading,
};

/* ---------------- utils ----------------- */
function normalizeQuestion(q) {
  if (!q) return null;
  if (q.options) return q;
  const opts = [];
  if (q.answera) opts.push(q.answera);
  if (q.answerb) opts.push(q.answerb);
  if (q.answerc) opts.push(q.answerc);
  if (q.answerd) opts.push(q.answerd);
  if (q.answere) opts.push(q.answere);
  if (q.answerf) opts.push(q.answerf);
  return {
    id: q.id,
    type: ["", "judge", "single", "multi"][q.type] || "single",
    title: q.questitle,
    options: opts,
    answer: q.yesanswer
      ? q.yesanswer.split("").map((x) => x.toUpperCase())
      : [],
  };
}

export default { namespaced: true, state, mutations, actions, getters };
